import { useState } from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { sqSummaryReportStore } from '@/core/core.stores';
import { ButtonGroup, Icon, Select, ToolbarButton } from '@seeqdev/qomponents';
import {
  setBaseline,
  setIsItemsOverlaid,
  setShowBellCurve,
  setShowBoxPlot,
  setShowBoxplotPoints,
  setShowCondition,
  setShowHistogram,
  setShowSignal,
  setShowTable,
  setShowTimeRange,
  setVisibleStats,
  setXAxisLabel,
  setYAxisLabel,
} from '@/summaryReport/summaryReport.actions';
import { useFlux } from '@/core/hooks/useFlux.hook';
import {
  BOX_PLOT_POINT_LABEL_OPTIONS,
  SUMMARY_TITLE_SHOW_HIDE_OPTIONS,
  X_AXIS_LABEL_OPTIONS,
  Y_AXIS_LABEL_OPTIONS,
} from '@/summaryReport/summaryData.constants';
import { LabelSelection } from '@/summaryReport/LabelSelection.molecule';
import { StatisticsSelection } from '@/summaryReport/StatisticsSelection.molecule';
import type { StatisticsSummaryItem } from '@/summaryReport/summaryData.types';

interface SummaryReportToolbarProps {
  size?: 'default' | 'sm' | 'xs';
}

export const SummaryReportToolbar: React.FunctionComponent<SummaryReportToolbarProps> = ({ size = 'default' }) => {
  const { t } = useTranslation();

  const [isLabelsPopoverOpen, setIsLabelsPopoverOpen] = useState(false);
  const [isTableStatsPopoverOpen, setIsTableStatsPopoverOpen] = useState(false);
  const {
    items,
    isItemsOverlaid,
    showHistogram,
    showTable,
    showBoxPlot,
    visibleStats,
    showBellCurve,
    yAxisLabel,
    xAxisLabel,
    baseline,
    showSignal,
    showTimeRange,
    showCondition,
    showBoxplotPoints,
  } = useFlux(sqSummaryReportStore);

  const isCompareMode = !_.isEmpty(baseline);

  const toggleVisibleStats = (key: string) => {
    const newVisibleStats = _.includes(visibleStats, key) ? _.without(visibleStats, key) : [...visibleStats, key];
    setVisibleStats(newVisibleStats);
  };

  const formatOptionLabel = (item: StatisticsSummaryItem) => (
    <div className="flexColumnContainer">
      <Icon icon="fc-statistic-summary" type="color" color={item.color} extraClassNames="mt3" />
      <span className="ml5">{item.name}</span>
    </div>
  );

  const viewButtonsConfiguration = [
    {
      id: 'overlayChartsBtn',
      active: isItemsOverlaid,
      icon: 'fa-arrow-right-arrow-left',
      label: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.OVERLAY'),
      tooltipText: _.isEmpty(baseline)
        ? t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.OVERLAY_TOOLTIP')
        : t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.OVERLAY_WITH_BASELINE_TOOLTIP'),
      onClick: () => setIsItemsOverlaid(!isItemsOverlaid),
      trackAction: 'overlay Statistics Summary',
    },
    {
      id: 'showHistogramBtn',
      active: showHistogram,
      icon: 'fc-bar-chart',
      label: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.HISTOGRAM'),
      tooltipText: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.HISTOGRAM_TOOLTIP'),
      onClick: () => setShowHistogram(!showHistogram),
      trackAction: 'overlay Statistics Summary',
    },
    {
      id: 'showBoxPlotBtn',
      active: showBoxPlot,
      icon: 'fa-bars',
      label: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.BOXPLOT'),
      tooltipText: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.BOXPLOT_TOOLTIP'),
      onClick: () => setShowBoxPlot(!showBoxPlot),
      trackAction: 'overlay Statistics Summary',
    },
    {
      id: 'showTableBtn',
      active: showTable,
      icon: 'fc-table',
      label: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.TABLE'),
      tooltipText: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.TABLE_TOOLTIP'),
      onClick: () => setShowTable(!showTable),
      trackAction: 'overlay Statistics Summary',
    },
  ];

  return (
    <div className="flexColumnContainer flexAlignBottom">
      <ButtonGroup
        extraClassNames="forceFlex flexCenter widthUnset pl3 pr3 button-group-br"
        buttons={[
          ..._.map(viewButtonsConfiguration, (config) => ({
            variant: 'element' as const,
            element: (
              <ToolbarButton
                key={config.id}
                isActive={config.active}
                icon={clsx(config.icon, 'fa-lg')}
                label={config.label}
                id={config.id}
                testId={config.id}
                tooltipText={config.tooltipText}
                tooltipOptions={{ position: 'top' }}
                onClick={config.onClick}
              />
            ),
          })),
        ]}
      />
      <ToolbarButton
        isActive={showBellCurve}
        icon={clsx('fc-bell-curve', 'fa-lg')}
        label={t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.BELL_CURVE')}
        id="showBellCurveBtn"
        testId="showBellCurveBtn"
        tooltipText={
          !showHistogram
            ? t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.BELL_CURVE_DISABLED_TOOLTIP')
            : t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.BELL_CURVE_TOOLTIP')
        }
        tooltipOptions={{ position: 'top' }}
        onClick={() => setShowBellCurve(!showBellCurve)}
        disabled={!showHistogram}
      />
      <ToolbarButton
        icon="fc-add-column"
        onClick={() => setIsTableStatsPopoverOpen(!isTableStatsPopoverOpen)}
        onHide={() => setIsTableStatsPopoverOpen(false)}
        isActive={isTableStatsPopoverOpen}
        disabled={!showTable}
        label={t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.STATS')}
        tooltipText={
          !showTable
            ? t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.STATS_DISABLED_TOOLTIP')
            : t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.STATS_TOOLTIP')
        }
        popoverContent={
          <StatisticsSelection
            visibleStats={visibleStats}
            toggleVisibleStat={toggleVisibleStats}
            isCompareMode={isCompareMode}
          />
        }
      />
      <ToolbarButton
        icon="fa-tag"
        onClick={() => setIsLabelsPopoverOpen(!isLabelsPopoverOpen)}
        onHide={() => setIsLabelsPopoverOpen(false)}
        label={t('TOOLBAR.LABELS')}
        isActive={isLabelsPopoverOpen}
        testId="summaryReportLabelsButton"
        popoverContent={
          <div className="flexRowContainer">
            {showHistogram && (
              <div>
                <div className="optionHeader min-width-300">
                  {t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.HISTOGRAM')}
                </div>
                <LabelSelection
                  options={X_AXIS_LABEL_OPTIONS}
                  setProperty={setXAxisLabel as any}
                  property="xAxisLabel"
                  labelValue={xAxisLabel}
                  labelTitle="INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.X_AXIS_TITLE"
                />
                <LabelSelection
                  options={Y_AXIS_LABEL_OPTIONS}
                  setProperty={setYAxisLabel as any}
                  property="yAxisLabel"
                  labelValue={yAxisLabel}
                  labelTitle="INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.Y_AXIS_TITLE"
                />
              </div>
            )}
            {showBoxPlot && (
              <div>
                <div className="optionHeader min-width-300">
                  {t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.BOXPLOT')}
                </div>
                <LabelSelection
                  options={BOX_PLOT_POINT_LABEL_OPTIONS}
                  setProperty={setShowBoxplotPoints as any}
                  property="pointLabel"
                  labelValue={showBoxplotPoints}
                  labelTitle="INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.LABEL.PLOT_LABEL"
                />
              </div>
            )}
            <div className="optionHeader min-width-300">
              {t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.SUMMARY_TITLE.HEADER')}
            </div>
            <LabelSelection
              options={SUMMARY_TITLE_SHOW_HIDE_OPTIONS}
              setProperty={setShowTimeRange}
              property="timeRange"
              labelValue={showTimeRange}
              labelTitle="INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.SUMMARY_TITLE.TIME_RANGE"
            />
            <LabelSelection
              options={SUMMARY_TITLE_SHOW_HIDE_OPTIONS}
              setProperty={setShowSignal}
              property="signal"
              labelValue={showSignal}
              labelTitle="INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.SUMMARY_TITLE.SIGNAL"
            />
            <LabelSelection
              options={SUMMARY_TITLE_SHOW_HIDE_OPTIONS}
              setProperty={setShowCondition}
              property="condition"
              labelValue={showCondition}
              labelTitle="INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.SUMMARY_TITLE.CONDITION"
            />
          </div>
        }
      />
      <div className="flexRowContainer min-width-200 mr5 ml5">
        <span>{t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.BASELINE')}:</span>
        <Select
          id="srToolbarBaseline"
          placeholder={t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TOOLBAR.BASELINE_PLACEHOLDER')}
          value={baseline}
          options={items}
          small={true}
          isClearable={true}
          onChange={(option) => setBaseline(option)}
          getOptionLabel={formatOptionLabel}
          getSelectedValueLabel={formatOptionLabel}
          controlClassNames="height-30 width-220"
        />
      </div>
    </div>
  );
};
